import { createSlice, PayloadAction, PrepareAction } from '@reduxjs/toolkit'

interface Connection {
  id: string
  [key: string]: unknown
}

interface AuthState {
  loading: boolean
  connectionsLoading: boolean
  accessToken: string | null
  refreshToken: string | null
  isEmailSent: boolean
  errorMessage: string
  showLogoutDisclaimerModal: boolean
  invitations: unknown[]
  connections: Connection[]
  error?: unknown
}

const initialState: AuthState = {
  loading: false,
  connectionsLoading: false,
  accessToken: null,
  refreshToken: null,
  isEmailSent: false,
  errorMessage: '',
  showLogoutDisclaimerModal: false,
  invitations: [],
  connections: [],
}

const authSlice = createSlice({
  name: 'authentication',
  initialState,
  reducers: {
    sendEmailSuccess: (state) => {
      state.isEmailSent = true
      state.errorMessage = ''
    },
    changeEmailInput: (state) => {
      state.isEmailSent = false
      state.errorMessage = ''
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload
    },
    loginSuccess: {
      prepare(accessToken: string, refreshToken: string) {
        return { payload: { accessToken, refreshToken } }
      },
      reducer(state, action: PayloadAction<{ accessToken: string; refreshToken: string }>) {
        state.accessToken = action.payload.accessToken
        state.refreshToken = action.payload.refreshToken
        state.loading = false
        state.errorMessage = ''
      },
    },
    loginFail: (state, action: PayloadAction<string>) => {
      state.errorMessage = action.payload
      state.loading = false
    },
    reset: () => initialState,
    clearLoginMessage: (state) => {
      state.loading = false
      state.isEmailSent = false
      state.errorMessage = ''
    },
    openLogoutModal: (state) => {
      state.showLogoutDisclaimerModal = true
    },
    hideLogoutModal: (state) => {
      state.showLogoutDisclaimerModal = false
    },
    logout: () => initialState,
    checkAccountByEmail: (state, action: PayloadAction<boolean>) => {
      state.isEmailSent = action.payload
    },
    sendEmailVerificationSuccess: () => {},
    sendEmailVerificationFail: (state, action: PayloadAction<string>) => {
      state.errorMessage = action.payload
    },
    validateEmailVerificationCodeSuccess: () => {},
    validateEmailVerificationCodeFail: (state, action: PayloadAction<string>) => {
      state.errorMessage = action.payload
    },
    sendInvitationsRequest: (state) => {
      state.loading = true
    },
    sendInvitationsSuccess: (state, action: PayloadAction<unknown[]>) => {
      state.invitations = action.payload
      state.loading = false
    },
    sendInvitationsFailure: (state, action: PayloadAction<unknown>) => {
      state.error = action.payload
      state.loading = false
    },
    loadingConnections: (state) => {
      state.connectionsLoading = true
    },
    loadingConnectionsSuccess: (state, action: PayloadAction<{ data: { connections: Connection[] } }>) => {
      state.connections = action.payload.data.connections
      state.connectionsLoading = false
    },
    loadingConnectionsFailure: (state, action: PayloadAction<unknown>) => {
      state.error = action.payload
      state.connectionsLoading = false
    },
    deleteConnectionSuccess: (state, action: PayloadAction<string>) => {
      state.connections = state.connections.filter(
        (connection) => connection.id !== action.payload
      )
      state.loading = false
      state.connectionsLoading = false
    },
    deleteConnectionFail: (state, action: PayloadAction<string>) => {
      state.errorMessage = action.payload
      state.loading = false
      state.connectionsLoading = false
    },
  },
})

export const authActions = authSlice.actions
export const authReducer = authSlice.reducer
// Export Types for backward compatibility (used by purgeOnLogoutMiddleware)
export const Types = Object.fromEntries(
  Object.entries(authSlice.actions).map(([key, action]) => [
    key.replace(/([A-Z])/g, '_$1').toUpperCase().replace(/^_/, ''),
    action.type,
  ])
)
export default authActions
